import { chain } from '@/config/chain'
import { getSmartWalletsConfig } from '@/config/paymaster'
import { appId, privyConfig } from '@/config/privy'
import { wagmiConfig } from '@/config/wagmi'
import { runWithTimeout } from '@/utils/runWithTimeout'
import { type PrivyClientConfig, PrivyProvider, usePrivy } from '@privy-io/react-auth'
import { SmartWalletsProvider, useSmartWallets } from '@privy-io/react-auth/smart-wallets'
import { WagmiProvider } from '@privy-io/wagmi'
import { confirmSignIn, fetchAuthSession, signIn, signUp } from 'aws-amplify/auth'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

// SmartWalletsProvider用のPaymaster設定を取得
// 環境変数でpaymasterContextが設定されている場合はそれを使用
// 設定がない場合はPrivy Dashboardの設定が使用される
const smartWalletsConfig = getSmartWalletsConfig()

// スマートウォレット初期化のタイムアウト。RPC障害で getClientForChain() が
// ハングする場合に、ユーザーが無限ローディングに閉じ込められるのを防ぐ。
const SMART_WALLET_INIT_TIMEOUT_MS = 15_000

// Privy 埋め込みウォレット作成のタイムアウト。通常は数秒で完了するが、
// Privy 側のネットワーク不調時にハングしないよう上限を設ける。
const EMBEDDED_WALLET_CREATE_TIMEOUT_MS = 15_000

// タイムアウト識別用のエラーメッセージ定数(UI 側で分岐に使う)。
const SMART_WALLET_INIT_TIMEOUT_ERROR = 'SMART_WALLET_INIT_TIMEOUT'
const EMBEDDED_WALLET_CREATE_TIMEOUT_ERROR = 'EMBEDDED_WALLET_CREATE_TIMEOUT'

interface WalletContextType {
  isAuthenticated: boolean
  isReady: boolean
  embeddedWalletAddress: string | null
  smartWalletAddress: string | null
  isSmartWalletReady: boolean
  walletError: string | null
  cognitoAuthError: string | null
  isCognitoAuthenticating: boolean
  isCognitoAuthenticated: boolean
  isCreatingEmbeddedWallet: boolean
  isPreparingSmartWallet: boolean
  login: () => void
  logout: () => Promise<void>
  retrySmartWallet: () => void
}

const WalletContext = createContext<WalletContextType>({
  isAuthenticated: false,
  isReady: false,
  embeddedWalletAddress: null,
  smartWalletAddress: null,
  isSmartWalletReady: false,
  walletError: null,
  cognitoAuthError: null,
  isCognitoAuthenticating: false,
  isCognitoAuthenticated: false,
  isCreatingEmbeddedWallet: false,
  isPreparingSmartWallet: false,
  login: () => {},
  logout: async () => {},
  retrySmartWallet: () => {},
})

function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    authenticated: privyAuthenticated,
    ready: privyReady,
    login,
    user,
    getAccessToken,
    createWallet,
    logout: privyLogout,
  } = usePrivy()
  const { getClientForChain } = useSmartWallets()
  const [walletError, setWalletError] = useState<string | null>(null)
  const [smartWalletAddress, setSmartWalletAddress] = useState<string | null>(null)
  const [isSmartWalletReady, setIsSmartWalletReady] = useState<boolean>(false)
  const [cognitoAuthError, setCognitoAuthError] = useState<string | null>(null)
  const [isCognitoAuthenticating, setIsCognitoAuthenticating] = useState<boolean>(false)
  const [isCognitoAuthenticated, setIsCognitoAuthenticated] = useState<boolean>(false)
  const [isCreatingEmbeddedWallet, setIsCreatingEmbeddedWallet] = useState<boolean>(false)
  const [isPreparingSmartWallet, setIsPreparingSmartWallet] = useState<boolean>(false)
  const [smartWalletAttempt, setSmartWalletAttempt] = useState<number>(0)

  // Privy認証情報を利用してCognito認証を行うコールバック関数
  const handleCognitoLogin = useCallback(
    async (privyDid: string) => {
      // 1. Privy DIDがない場合は認証を中断
      if (!privyDid) return
      console.log('privyDid', privyDid)
      const sanitizedPrivyDid = privyDid.replace('did:privy:', '')
      console.log('sanitizedPrivyDid', sanitizedPrivyDid)

      let privyAccessToken: string | null = null

      setIsCognitoAuthenticating(true)
      setCognitoAuthError(null)

      // 2. Cognito認証によるSignInを試行
      try {
        console.log('Attempting Cognito SignIn...')

        // 2.1. Privyアクセストークンを取得
        privyAccessToken = await getAccessToken()
        if (!privyAccessToken) {
          throw new Error('Privy access token not found')
        }
        console.log('privyAccessToken', privyAccessToken)

        // 2.2. カスタムチャレンジによる認証フローを開始: パスワードなし
        const { nextStep } = await signIn({
          username: sanitizedPrivyDid,
          options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP',
          },
        })
        console.log('Sign-in initiated. Next step:', nextStep.signInStep)

        // 2.3. カスタムチャレンジの応答
        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          console.log('Responding to Custom Challenge with Privy data...')

          // カスタムチャレンジの回答を送信。Verify Auth Challenge Lambdaに渡すデータを指定。
          const confirmOutput = await confirmSignIn({
            challengeResponse: privyAccessToken,
          })

          // 認証成功の確認
          if (confirmOutput.isSignedIn) {
            console.log(
              'Authentication successful! Session managed by Amplify. After this, you can use the ID Token.'
            )
            setCognitoAuthError(null)
            setIsCognitoAuthenticated(true)
          } else {
            console.error(
              'Authentication not complete. Next step:',
              confirmOutput.nextStep.signInStep
            )
            throw new Error(
              `Authentication failed at confirmation step: ${confirmOutput.nextStep.signInStep}`
            )
          }
        } else if (nextStep.signInStep === 'DONE') {
          // ユーザーが既にサインイン済みの場合は認証を完了
          console.log('User already signed in.')
          setCognitoAuthError(null)
        } else {
          throw new Error(`Unexpected sign-in step: ${nextStep.signInStep}`)
        }
      } catch (error) {
        console.log('Cognito SignIn Failed:', error)

        // 2.4. Cognito認証によるSignUpを試行
        try {
          console.log('Attempting Cognito SignUp...')
          const signUpOutput = await signUp({
            username: sanitizedPrivyDid,
            password: 'deadBEEF&1234567890', // ダミーの値。実際には使われない
          })
          if (signUpOutput.isSignUpComplete) {
            console.log('Cognito SignUp successful! Re-attempting Cognito SignIn...')
            setCognitoAuthError(null)
            if (!privyAccessToken) {
              throw new Error('Privy access token not available after sign-up')
            }

            const { nextStep: nextStepAfterSignUp } = await signIn({
              username: sanitizedPrivyDid,
              options: {
                authFlowType: 'CUSTOM_WITHOUT_SRP',
              },
            })
            console.log(
              'Sign-in initiated after sign-up. Next step:',
              nextStepAfterSignUp.signInStep
            )

            if (nextStepAfterSignUp.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
              const confirmAfterSignUp = await confirmSignIn({
                challengeResponse: privyAccessToken,
              })

              if (confirmAfterSignUp.isSignedIn) {
                console.log('Authentication successful after sign-up!')
                setIsCognitoAuthenticated(true)
              } else {
                console.error(
                  'Authentication not complete after sign-up. Next step:',
                  confirmAfterSignUp.nextStep.signInStep
                )
                throw new Error(
                  `Authentication failed after sign up: ${confirmAfterSignUp.nextStep.signInStep}`
                )
              }
            } else if (nextStepAfterSignUp.signInStep === 'DONE') {
              console.log('User already signed in after sign-up.')
              setIsCognitoAuthenticated(true)
            } else {
              throw new Error(
                `Unexpected sign-in step after sign-up: ${nextStepAfterSignUp.signInStep}`
              )
            }
          } else {
            throw new Error(`Cognito SignUp failed: ${signUpOutput.nextStep.signUpStep}`)
          }
        } catch (error) {
          console.error('Cognito SignUp Failed:', error)
          setCognitoAuthError(error instanceof Error ? error.message : '認証エラー')
          setIsCognitoAuthenticated(false)
          throw error
        }
      } finally {
        setIsCognitoAuthenticating(false)
      }
    },
    [getAccessToken]
  )

  // Cognito認証状態をチェックするコールバック関数
  const checkCognitoAuthStatus = useCallback(async () => {
    try {
      const session = await fetchAuthSession()
      if (session.tokens?.accessToken) {
        console.log(
          'Cognito authentication status: authenticated',
          session.tokens.accessToken.toString()
        )
        console.log('ID Token:', session.tokens.idToken?.toString())
        setIsCognitoAuthenticated(true)
        setCognitoAuthError(null)
        return true
      }
      setIsCognitoAuthenticated(false)
      setCognitoAuthError(null)
      return true
    } catch {
      setIsCognitoAuthenticated(false)
      return false
    }
  }, [])

  // スマートウォレットの準備を監視
  useEffect(() => {
    if (user?.smartWallet?.address) {
      setSmartWalletAddress(user.smartWallet.address)
      setIsSmartWalletReady(true)
    }
  }, [user?.smartWallet?.address])

  // コンポーネント初期化時に認証状態をチェック
  useEffect(() => {
    checkCognitoAuthStatus()
  }, [checkCognitoAuthStatus])

  // Privy認証完了後に埋め込みウォレットが無ければ作成する
  useEffect(() => {
    if (!privyReady || !privyAuthenticated) return
    if (user?.wallet?.address || isCreatingEmbeddedWallet) return

    let cancelled = false
    setIsCreatingEmbeddedWallet(true)
    setWalletError(null)

    runWithTimeout(
      createWallet(),
      EMBEDDED_WALLET_CREATE_TIMEOUT_MS,
      EMBEDDED_WALLET_CREATE_TIMEOUT_ERROR
    )
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to create embedded wallet via Privy:', error)
        const isTimeout =
          error instanceof Error && error.message === EMBEDDED_WALLET_CREATE_TIMEOUT_ERROR
        setWalletError(
          isTimeout
            ? 'ネットワークが不安定でウォレットを作成できませんでした。通信環境を確認して再試行してください。'
            : 'ウォレットの作成に失敗しました。再試行するか、ログインし直してください。'
        )
      })
      .finally(() => {
        if (cancelled) return
        setIsCreatingEmbeddedWallet(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    privyReady,
    privyAuthenticated,
    user?.wallet?.address,
    createWallet,
    isCreatingEmbeddedWallet,
  ])

  // Privy認証完了後にスマートウォレットクライアントを初期化する。
  // RPC障害時にハングしないよう、各試行にタイムアウト(15秒)を設ける。
  // 失敗時は walletError を立て、UI 側で再試行導線(retrySmartWallet)を出す。
  // smartWalletAttempt は retrySmartWallet() からの再実行トリガー。エフェクト本体で
  // 参照することで biome の useExhaustiveDependencies も自然に満たす。
  useEffect(() => {
    if (!privyReady || !privyAuthenticated) return
    if (user?.smartWallet?.address) return
    // smartWalletAttempt を deps として有効にするための参照(値は使わない)。
    void smartWalletAttempt

    let cancelled = false

    const ensureSmartWallet = async () => {
      try {
        setIsPreparingSmartWallet(true)
        setWalletError(null)
        await runWithTimeout(
          getClientForChain({ id: chain.id }),
          SMART_WALLET_INIT_TIMEOUT_MS,
          SMART_WALLET_INIT_TIMEOUT_ERROR
        )
      } catch (error) {
        // ログ出力は常に行う(debug 性確保)。
        console.error('Failed to prepare smart wallet client:', error)
        if (cancelled) return

        const isTimeout =
          error instanceof Error && error.message === SMART_WALLET_INIT_TIMEOUT_ERROR
        // 即時 reject(Privy 内部の最適化的呼び出しが失敗するパス)は非致命:
        // Privy はスマートウォレットアドレスを別経路で生成し、その後 user
        // オブジェクト経由で address が populate される(通常 1〜3 秒程度)。
        // ここで walletError を立てると、その間にエラー UI が一瞬表示される。
        // 本当に詰まっているケースは下の watchdog effect が拾う。
        if (!isTimeout) return

        setWalletError(
          'ネットワークが不安定でウォレットを準備できませんでした。通信環境を確認して再試行してください。'
        )
      } finally {
        if (!cancelled) setIsPreparingSmartWallet(false)
      }
    }

    ensureSmartWallet()

    return () => {
      cancelled = true
    }
  }, [
    privyReady,
    privyAuthenticated,
    user?.smartWallet?.address,
    getClientForChain,
    smartWalletAttempt,
  ])

  // ユーザー操作によるスマートウォレット初期化の再試行。
  // smartWalletAttempt を更新することで上の useEffect を再実行させる。
  // walletError を同期的にクリアすると、次レンダーで PreLogin がエラー画面から
  // 抜けるが useEffect が走る前の 1 レンダーだけオンボーディング画面にフォールスルー
  // してしまうため、isPreparingSmartWallet も同期的に true にしておく。
  const retrySmartWallet = useCallback(() => {
    // 前の試行がまだ動いているときは何もしない(ボタン連打防止の保険)。
    if (isPreparingSmartWallet) return
    setWalletError(null)
    setIsPreparingSmartWallet(true)
    setSmartWalletAttempt((n) => n + 1)
  }, [isPreparingSmartWallet])

  // Watchdog: Privy 認証完了から 15 秒経っても smartWallet.address が populate
  // されない場合、本当に詰まっていると判断して walletError を立てる。
  // ensureSmartWallet の catch を緩めた(即時 reject はスルー)代わりの安全網。
  // - getClientForChain が即 reject + Privy address 生成も失敗 → ここで検知
  // - 通常パス(Privy が address を populate)では isSmartWalletReady=true で
  //   この effect の cleanup が timer を解除するため、誤検知しない。
  useEffect(() => {
    if (!privyAuthenticated || !privyReady) return
    if (isSmartWalletReady) return
    if (walletError) return

    const timeout = setTimeout(() => {
      setWalletError(
        'ネットワークが不安定でウォレットの準備が完了しませんでした。再試行するか、ログインし直してください。'
      )
    }, SMART_WALLET_INIT_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [privyAuthenticated, privyReady, isSmartWalletReady, walletError])

  // Cognito認証が未認証の場合、スマートウォレットが準備できたらCognito認証を実行
  useEffect(() => {
    if (
      isSmartWalletReady &&
      user?.id &&
      !isCognitoAuthenticating &&
      !cognitoAuthError &&
      !isCognitoAuthenticated
    ) {
      handleCognitoLogin(user.id)
    }
  }, [
    isSmartWalletReady,
    user?.id,
    isCognitoAuthenticating,
    cognitoAuthError,
    isCognitoAuthenticated,
    handleCognitoLogin,
  ])

  // スマートウォレットが準備できたら、それまでのエラーをクリアする。
  useEffect(() => {
    if (isSmartWalletReady && walletError) {
      setWalletError(null)
    }
  }, [isSmartWalletReady, walletError])

  const clearBrowserStorage = useCallback(() => {
    if (typeof window === 'undefined') return

    try {
      window.localStorage.clear()
    } catch (error) {
      console.error('Failed to clear localStorage on logout:', error)
    }

    try {
      const cookies = document.cookie ? document.cookie.split(';') : []
      for (const cookie of cookies) {
        const eqIndex = cookie.indexOf('=')
        const name = eqIndex > -1 ? cookie.slice(0, eqIndex) : cookie
        document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
      }
    } catch (error) {
      console.error('Failed to clear cookies on logout:', error)
    }
  }, [])

  const handleLogout = useCallback(async () => {
    try {
      await privyLogout()
    } finally {
      setSmartWalletAddress(null)
      setIsSmartWalletReady(false)
      setIsCreatingEmbeddedWallet(false)
      setIsPreparingSmartWallet(false)
      setIsCognitoAuthenticating(false)
      setIsCognitoAuthenticated(false)
      setWalletError(null)
      setCognitoAuthError(null)
      setSmartWalletAttempt(0)
      clearBrowserStorage()
    }
  }, [privyLogout, clearBrowserStorage])

  // コンテキストの値を含めたオブジェクトを返す
  const value: WalletContextType = {
    isAuthenticated: privyAuthenticated && privyReady && isCognitoAuthenticated,
    isReady: privyReady,
    embeddedWalletAddress: user?.wallet?.address || null,
    smartWalletAddress,
    isSmartWalletReady,
    walletError,
    cognitoAuthError,
    isCognitoAuthenticating,
    isCognitoAuthenticated,
    isCreatingEmbeddedWallet,
    isPreparingSmartWallet,
    login,
    logout: handleLogout,
    retrySmartWallet,
  }

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
}

export function WalletProvider({
  children,
  config = privyConfig,
}: {
  children: React.ReactNode
  // cross-app ページなど、Privy ログインモーダルの appearance を差し替えたい場合に上書きする。
  config?: PrivyClientConfig
}) {
  return (
    <PrivyProvider appId={appId} config={config}>
      <SmartWalletsProvider config={smartWalletsConfig}>
        <AuthProvider>
          <WagmiProvider config={wagmiConfig}>{children}</WagmiProvider>
        </AuthProvider>
      </SmartWalletsProvider>
    </PrivyProvider>
  )
}

export const useWallet = () => useContext(WalletContext)
